import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { View, Share, ScrollView, PixelRatio, StyleSheet, ViewProps, FlatList } from 'react-native';
import { TouchableRipple, useTheme } from 'react-native-paper';
import { addSeconds, format as formatDateFns, differenceInMinutes } from 'date-fns';
import { useNavigation } from '@react-navigation/native';
import Typography from '../../components/Typography';
import { useMapViewFilter } from '../../context/MapViewFilterContext';
import { useExtractedReservationData, useSubscribeToSinglePodReservationChange } from '../../apis/reservationApis';
import { AuthState, useAuth } from '../../context/AuthContext';
import { BookedCardContent, BookedCardFooter } from './BookedCardContent';
import { BookedNotNowCardContent } from './BookedNotNowCardContent';
import { AvailableCardContent } from './AvailableCardContent';
import { UnauthenticatedCardContent } from './UnauthenticatedCardContent';
import { useGetSpaceInfoWithPricing } from '../../payment/paymentApis';
import { useI18n } from '../../context/I18nContext';
import { screenNames } from '../../navigators/screenNames';
import { useMixpanel } from '../../mixpanel/MixpanelContext';
import { generateDeepLinkForSpace } from '../../utils/urlHandler';
import { useNowMinutes } from '../../hooks/useNow';
import { Pod } from '../../types/misc.types';
import useScreenSize from '../../hooks/useScreenSize';
import Button, { ButtonProps } from '../../components/Button';
import Spacer from '../../components/Spacer';
import Container from '../../components/Container';
import { UiIcon, UiIconProps } from '../../components/SimpleListIcon';
import { AppDrawerNavigationProp } from '../../components/DrawerMenuContent';
import ContextMenuModal from '../../components/modal/ContextMenuModal';
import {
  BillingType,
  Maybe,
  PodStatus,
  Pois,
  PoisType,
  ProductSubscriptionStatus,
  ReservationStatus,
  SubscriptionOnUpdateReservationArgs,
} from '../../types/appsync-types';
import { CardBadge } from '../../components/CardBadge';
import GradientButton from '../../components/GradientButton';
import { useMyProductReservations } from '../../apis/subscriptionApis';
import { UnpaidReservationContent } from './UnpaidReservationContent';
import { InactiveReservationContent } from './InactiveReservationContent';
import { GeoJsonWithReservation } from '../../utils/geojsonConverter';
import { useGetPoisByParentId } from '../../apis/mapApis';

const ServiceTable = ({ pod, lockerInfo }: { pod: Pod; lockerInfo: GeoJsonWithReservation | null }) => {
  const { colors } = useTheme();
  const { I18n } = useI18n();
  const { filter } = useMapViewFilter();

  const navigation = useNavigation<AppDrawerNavigationProp>();

  const getLockersResult = useGetPoisByParentId(lockerInfo?.localRef ?? '', { from: filter.date.toISOString() });

  const {
    ongoingReservations: ongoingProductReservations,
    unpaidReservations: unpaidProductReservations,
    upcomingReservations: upcomingProductReservations,
  } = useMyProductReservations();

  const lockerReservation = useMemo(() => {
    const lockerResults = getLockersResult?.data?.data?.getPoisByParentId ?? [];
    const reservedLocker = lockerResults.find(
      (locker) => locker.availabilityStatus?.status === PodStatus.RESERVED_BY_ME,
    );

    const ongoingLockerReservation =
      reservedLocker &&
      ongoingProductReservations?.find((reservation) =>
        reservation?.product?.linkedPods?.some((linkedPod) => linkedPod?.localRef === reservedLocker.localRef),
      );

    if (ongoingLockerReservation) return ongoingLockerReservation;

    const upcomingLockerReservation =
      lockerResults &&
      upcomingProductReservations?.find((reservation) =>
        reservation?.product?.linkedPods?.some((linkedPod) =>
          lockerResults.find((locker) => linkedPod?.localRef === locker.localRef),
        ),
      );

    if (upcomingLockerReservation) return upcomingLockerReservation;

    const unpaidLockerReservation =
      lockerResults &&
      unpaidProductReservations?.find((reservation) =>
        reservation?.product?.linkedPods?.some((linkedPod) =>
          lockerResults.find((locker) => linkedPod?.localRef === locker.localRef),
        ),
      );

    return unpaidLockerReservation;
  }, [ongoingProductReservations, upcomingProductReservations, unpaidProductReservations, getLockersResult]);

  const tableAmenities: TableAmenity[] = useMemo(() => {
    const lockers = (getLockersResult?.data?.data?.getPoisByParentId ?? []).filter(
      (locker) => locker.availabilityStatus?.status === PodStatus.FREE,
    );

    return [
      {
        icon: { name: 'bicycle', set: 'material-community' },
        title: I18n.t('mapview.spaceInfo.amenities.bicycleParking'),
        value: pod?.metadata?.maxCapacity ?? undefined,
      },
      {
        icon: { name: 'locker-multiple', set: 'material-community' },
        title: I18n.t('mapview.spaceInfo.amenities.lockers'),
        value: pod?.metadata?.lockers,
        skip: !pod?.metadata?.lockers,
        action: lockerReservation
          ? // () =>
            // Constants.manifest?.extra?.disableBitwards ? (
            //   <GetFromStoreButton
            //     style={{ marginLeft: 'auto' }}
            //     buttonStyle={{ height: 'unset' as any }}
            //     onUnknownStore={() => {
            //       navigation.navigate('map', { screen: screenNames.ChooseStoreDialog });
            //     }}
            //   >
            //     {I18n.t('storeDialog.installToUnlockButton')}
            //   </GetFromStoreButton>
            // ) : (
            //   <UnlockButton
            //     resources={ongoingLockerReservation?.resources}
            //     style={{ marginLeft: 'auto' }}
            //     buttonStyle={{
            //       height: 'unset' as any,
            //       minHeight: 'unset' as any,
            //       paddingVertical: 2,
            //       paddingHorizontal: 0,
            //     }}
            //     onPress={() => {
            //       mp?.track('Unlock attempt from MapView booked card', flattenDict('space', ongoingLockerReservation));
            //     }}
            //     loading={isReservationDataLoading}
            //   />
            // )
            {
              text: I18n.t('mapview.podInfoCard.ongoingLockerReservation'),
              icon: 'login',
              function: () => {
                if (lockerInfo?.localRef) {
                  navigation.navigate('map', {
                    screen: screenNames.ReservationProductDetailScreen,
                    params: {
                      productId: lockerReservation.productId,
                    },
                  });
                }
              },
              disabled:
                lockerReservation.status !== ProductSubscriptionStatus.ACTIVE &&
                lockerReservation.status !== ProductSubscriptionStatus.RESERVED &&
                lockerReservation.status !== ProductSubscriptionStatus.ENDED_NOT_PAID &&
                lockerReservation.status !== ProductSubscriptionStatus.REQUIRES_ACTION,
            }
          : {
              text: I18n.t('mapview.podInfoCard.book'),
              icon: 'calendar',
              function: () => {
                if (lockerInfo?.localRef) {
                  navigation.navigate('map', {
                    screen: screenNames.LockerListingScreen,
                    params: {
                      podId: lockerInfo.localRef,
                    },
                  });
                } else if (lockers.length === 1) {
                  navigation.navigate('map', {
                    screen: screenNames.OneTimePurchaseProductsScreen,
                    params: {
                      podId: lockers[0]?.id,
                      podType: PoisType.LOCKER,
                    },
                  });
                }
              },
              disabled: lockerInfo?.podStatus !== PodStatus.FREE,
            },
      },
    ] as TableAmenity[];
  }, [
    I18n,
    getLockersResult?.data?.data?.getPoisByParentId,
    lockerInfo?.localRef,
    lockerInfo?.podStatus,
    navigation,
    lockerReservation,
    pod.metadata?.lockers,
    pod.metadata?.maxCapacity,
  ]);

  return (
    <View style={{ paddingHorizontal: 16, paddingTop: 16 }}>
      <Typography variant="sub2" color={colors.primary} style={{ fontWeight: '700' }}>
        {I18n.t('mapview.podInfoCard.availableServices')}
      </Typography>
      {tableAmenities.map((item, index) => {
        return item.skip ? null : (
          <View
            key={item.title}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: 6,
              borderColor: colors.primary,
              borderBottomWidth: index === tableAmenities.length - 1 ? 0 : 1,
            }}
          >
            <UiIcon
              {...item.icon}
              color={colors.primary}
              size={24}
              style={{
                borderColor: colors.primary,
                borderWidth: 1,
                paddingVertical: 2,
                paddingHorizontal: 4,
                borderRadius: 4,
              }}
            />
            <Typography variant="body2" style={{ marginLeft: 4 }}>
              {item.title}
            </Typography>
            {item.action &&
              (typeof item.action === 'function' ? (
                item.action()
              ) : (
                <GradientButton
                  disabled={item.action.disabled}
                  icon={item.action.icon}
                  style={{ marginLeft: 'auto' }}
                  buttonStyle={{
                    height: 'unset' as any,
                    paddingVertical: 2,
                    paddingHorizontal: 0,
                  }}
                  contentContainerStyle={{ width: 'unset' as any }}
                  onPress={item.action.function}
                >
                  {item.action.text}
                </GradientButton>
              ))}
          </View>
        );
      })}
    </View>
  );
};

export const CardTitleRow = ({ space, showInfoIcon }: { space: Pois; showInfoIcon?: boolean }) => {
  const { colors } = useTheme();

  const amenities: { icon: UiIconProps; value?: string | number; skip?: boolean }[] = [
    {
      icon: { name: 'bicycle', set: 'material-community' },
      value: space.metadata?.maxCapacity ?? 0,
      skip: space.spaceType === PoisType.LOCKER,
    },
    { icon: { name: 'locker-multiple', set: 'material-community' }, skip: !space.metadata?.lockers }, // TODO: Get locker count?
    { icon: { name: 'wrench', set: 'material-community' }, skip: !space.metadata?.serviceStations },
  ];

  const titleRowStyles = useMemo(
    () =>
      StyleSheet.create({
        amenityItem: {
          paddingEnd: 12,
        },
        amenityRow: { flexDirection: 'row', alignItems: 'center' },
        imagePodDetail: {
          width: 80,
          height: 80,
        },
        titleSection: {
          flexDirection: 'row',
        },
        title: {
          marginLeft: 16,
          flex: 1,
          flexDirection: 'column',
          position: 'relative',
          alignItems: 'flex-start',
        },
        badgeRow: {
          flex: 1,
          flexDirection: 'row',
          alignItems: 'flex-start',
          marginBottom: 8,
        },
      }),

    [],
  );

  return (
    <View style={titleRowStyles.titleSection}>
      {/* Content */}
      <View style={[titleRowStyles.title]}>
        {/* Space id badge */}
        <View style={titleRowStyles.badgeRow}>
          <CardBadge text={`#${space.localRef}`} />
          <Spacer type="column" />
        </View>
        {/* Title row */}
        <Typography variant={'h5'} color={colors.onSurface} style={{ marginBottom: 4 }}>
          {space.title ?? 'Biketti'}
        </Typography>
        {/* Amenities row */}
        <View style={titleRowStyles.amenityRow}>
          <FlatList
            data={amenities}
            horizontal
            contentContainerStyle={{ gap: 6 }}
            renderItem={({ item }) => {
              if (item.skip) return null;
              return (
                <CardBadge text={item.value} icon={item.icon} textStyle={{ color: colors.primary, fontWeight: 700 }} />
              );
            }}
          />
        </View>
      </View>
      {showInfoIcon ? <UiIcon name="info" color={colors.primary} size={32} style={{ paddingTop: 4 }} /> : null}
    </View>
  );
};

export const AmenityCapacity: FC<ViewProps & { count: Maybe<number> | undefined }> = ({ count, style, ...other }) => {
  const { colors } = useTheme();
  return (
    <View
      style={[
        {
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        },
        style,
      ]}
      {...other}
    >
      <Typography variant={'h6'} color={colors.onSurface}>
        {count ?? '?'}
      </Typography>
      <UiIcon
        style={{ marginLeft: 1 }}
        set={'material-community'}
        name={'bicycle'}
        color={colors.onSurface}
        size={21}
      />
    </View>
  );
};

export const AmenityIcon: FC<
  ViewProps & {
    name: 'maxCapacity' | 'screen' | 'whiteboard' | 'usbPower' | 'powerOutlet' | 'bikeWash';
    iconColor?: string;
  }
> = ({ name, style, iconColor, ...other }) => {
  const { colors } = useTheme();
  const color: string = iconColor ?? colors.onSurface;
  if (name === 'screen') {
    return <UiIcon name={'screen-desktop'} color={color} size={18} style={style} />;
  }
  if (name === 'powerOutlet') {
    return (
      <View
        style={[
          {
            flexDirection: 'row',
          },
          style,
        ]}
        {...other}
      >
        <UiIcon set="material-community" name="power-plug-outline" color={color} size={18} />
        <UiIcon
          name="lightning-bolt-outline"
          set="material-community"
          color={color}
          size={18}
          style={{
            marginLeft: -9,
            marginRight: -9,
          }}
        />
      </View>
    );
  }
  if (name === 'whiteboard') {
    return <UiIcon name="easel-outline" set="ion" color={color} size={18} style={style} />;
  }
  if (name === 'usbPower') {
    return (
      <View
        style={[
          {
            flexDirection: 'row',
          },
          style,
        ]}
        {...other}
      >
        <UiIcon set="material-community" name="usb-port" color={color} size={18} />
        <UiIcon
          set="material-community"
          name={'lightning-bolt-outline'}
          color={color}
          size={18}
          style={{
            marginLeft: -9,
            marginRight: -9,
          }}
        />
      </View>
    );
  }
  if (name === 'maxCapacity') {
    return <UiIcon set={'material-community'} name={'bicycle'} color={color} size={18} />;
  }
  if (name === 'bikeWash') {
    return <UiIcon set={'material-community'} name={'shower-head'} color={color} size={18} />;
  }
  return null;
};

type TableAmenity = {
  icon: UiIconProps;
  title: string;
  value?: number | string | null;
  skip?: boolean;
  action?:
    | (() => React.JSX.Element)
    | {
        text: string;
        icon?: ButtonProps['icon'];
        function: () => void;
        disabled: boolean;
      };
};

type PodInformationCardProps = {
  pod: Pod;
  lockerInfo: GeoJsonWithReservation | null;
  showServices: boolean;
  onBrowseServicesPress: () => void;
};

const PodInformationCard = ({ pod, lockerInfo, showServices, onBrowseServicesPress }: PodInformationCardProps) => {
  const { colors } = useTheme();
  const { I18n } = useI18n();
  const mp = useMixpanel();

  const navigation = useNavigation<AppDrawerNavigationProp>();
  const now = useNowMinutes();

  const { filter } = useMapViewFilter();
  const { state } = useAuth();
  const { windowHeight } = useScreenSize();

  const bookingCardFallback = useRef({ podId: pod.id, fallback: 'DEFAULT' });
  if (bookingCardFallback.current.podId !== pod.id) {
    bookingCardFallback.current = { podId: pod.id, fallback: 'DEFAULT' };
  }

  const currentFilter: { from: string; to: string } = useMemo(() => {
    return {
      from: filter.date.toISOString(),
      to: addSeconds(filter.date, filter.durationInSecond).toISOString(),
    };
  }, [filter.durationInSecond, filter.date]);

  const parameterForSubscription: SubscriptionOnUpdateReservationArgs = useMemo(() => {
    return {
      poisId: pod.id,
      reservedDay: formatDateFns(filter.date, 'yyyy-MM-dd'),
    };
  }, [filter.date, pod.id]);

  const { ongoingReservations, upcomingReservations } = useExtractedReservationData();

  const {
    isLoading: isProductReservationsQueryLoading,
    ongoingReservations: ongoingProductReservations,
    unpaidReservations: unpaidProductReservations,
    upcomingReservations: upcomingProductReservations,
    rest: restReservations,
  } = useMyProductReservations();

  const ongoingReservationForPod = useMemo(
    () => ongoingReservations.find((res) => res?.spaceId === pod.localRef),
    [ongoingReservations, pod.localRef],
  );
  const overlappingReservationForPod = useMemo(() => {
    return upcomingReservations.find((res) => {
      const t = filter.date;
      return res?.spaceId === pod.localRef && res.from <= t && res.to >= t;
    });
  }, [filter.date, pod.localRef, upcomingReservations]);

  const ongoingProductReservationForPod = useMemo(
    () =>
      ongoingProductReservations?.find((reservation) =>
        reservation?.product?.linkedPods?.some((linkedPod) => linkedPod?.localRef === pod.localRef),
      ),
    [ongoingProductReservations, pod],
  );

  const unpaidProductReservationForPod = useMemo(
    () =>
      unpaidProductReservations.find((reservation) =>
        reservation?.product?.linkedPods?.some((linkedPod) => linkedPod?.localRef === pod.localRef),
      ),
    [pod.localRef, unpaidProductReservations],
  );

  const upcomingReservationForPod = useMemo(
    () =>
      upcomingProductReservations.find((reservation) =>
        reservation?.product?.linkedPods?.some((linkedPod) => linkedPod?.localRef === pod.localRef),
      ),
    [pod.localRef, upcomingProductReservations],
  );

  const restReservationForPod = useMemo(
    () =>
      restReservations.find((reservation) =>
        reservation?.product?.linkedPods?.some((linkedPod) => linkedPod?.localRef === pod.localRef),
      ),
    [pod.localRef, restReservations],
  );

  useSubscribeToSinglePodReservationChange(parameterForSubscription, currentFilter);

  const podInformationQuery = useGetSpaceInfoWithPricing({
    input: {
      ...currentFilter,
      spaceId: pod.localRef!,
    },
  });

  const [isMenuRightVisible, setIsMenuRightVisible] = useState(false);

  const menuRightItems = [
    {
      label: I18n.t('share.shareMenu'),
      onPress: async () => {
        const deepLinkUrl = generateDeepLinkForSpace(pod.localRef!);
        setIsMenuRightVisible(false);
        const result = await Share.share({
          title: I18n.t('share.share'),
          message: `Biketti #${pod.localRef!} ${deepLinkUrl}`,
        });

        if (result.action === Share.sharedAction) {
          mp?.track('Shared bicyclehut link', {
            deepLinkUrl,
            pod: pod.localRef,
            activityType: result.activityType,
          });
          mp?.getPeople().increment('Bicyclehut links shared', 1);
        }
      },
    },
  ];

  /* Only switch card status when pod information query has succeeded */
  const bookingCard = useMemo(() => {
    if (state === AuthState.GUEST_LOGIN) {
      return 'LOGIN_CARD';
    }
    if (!podInformationQuery.isLoading && !isProductReservationsQueryLoading) {
      if (unpaidProductReservationForPod) {
        return 'UNPAID_RESERVATION_CARD';
      }
      if (
        podInformationQuery.data?.data?.getPoisByID?.availabilityStatus?.status === PodStatus.RESERVED_BY_ME ||
        upcomingReservationForPod
      ) {
        if (
          filter.date <= now &&
          now <= addSeconds(filter.date, filter.durationInSecond) &&
          ongoingReservationForPod?.status === ReservationStatus.RESERVED
        ) {
          return 'USER_BOOKED_FOR_CURRENT_TIME_CARD';
        }
        return 'USER_BOOKED_FOR_OTHER_TIME_CARD';
      }
      if (restReservationForPod) {
        return 'INACTIVE_RESERVATION_CARD';
      }
      return 'DEFAULT';
    }
    return bookingCardFallback.current.fallback;
  }, [
    filter,
    now,
    ongoingReservationForPod?.status,
    podInformationQuery.data?.data?.getPoisByID?.availabilityStatus?.status,
    podInformationQuery.isLoading,
    state,
    upcomingReservationForPod,
    unpaidProductReservationForPod,
    restReservationForPod,
    isProductReservationsQueryLoading,
  ]);
  // Fall back to previous booking card value while podInformationQuery is loading to keep child components mounted
  bookingCardFallback.current = { podId: pod.id, fallback: bookingCard };

  const renderCardContent = useCallback(() => {
    const minBookingTimeMins = pod.minimumBookingMinutes || 15;

    let freeForMinutes: number | undefined;
    const avail = podInformationQuery.data?.data?.getPoisByID?.availabilityStatus;
    if (avail) {
      if (avail.status === PodStatus.FREE && avail.nextFreeEnd) {
        const freeEndExclusive = new Date(avail.nextFreeEnd);
        freeForMinutes = differenceInMinutes(freeEndExclusive, filter.date);
      } else if (avail.status === PodStatus.FREE && !avail.nextFreeEnd) {
        freeForMinutes = undefined;
      } else {
        freeForMinutes = 0;
      }
    }
    switch (bookingCard) {
      case 'LOGIN_CARD':
        return (
          <UnauthenticatedCardContent
            spaceTimezone={pod.building?.timezone}
            minBookingMins={minBookingTimeMins}
            maxBookingMins={freeForMinutes}
            isPrepaid={
              pod.billingType === BillingType.PREPAID && pod.availabilityStatus?.status !== PodStatus.SUBSCRIPTION_ONLY
            }
            availabilityStatus={pod.availabilityStatus?.status}
          />
        );
      case 'USER_BOOKED_FOR_CURRENT_TIME_CARD':
        return (
          <BookedCardContent
            ongoingReservation={ongoingReservationForPod!}
            ongoingProductReservation={ongoingProductReservationForPod ?? undefined}
            loading={podInformationQuery.isLoading}
          />
        );
      case 'USER_BOOKED_FOR_OTHER_TIME_CARD':
        return (
          <BookedNotNowCardContent
            minBookingMins={minBookingTimeMins}
            maxBookingMins={freeForMinutes}
            reservation={overlappingReservationForPod}
            productReservation={upcomingReservationForPod}
            buildingTimezone={pod.building?.timezone}
          />
        );
      case 'UNPAID_RESERVATION_CARD':
        return <UnpaidReservationContent reservation={unpaidProductReservationForPod} podId={pod.localRef} />;
      case 'INACTIVE_RESERVATION_CARD':
        return <InactiveReservationContent reservation={restReservationForPod} podId={pod.localRef} />;
      default:
        return (
          <AvailableCardContent
            space={pod}
            minBookingMins={minBookingTimeMins}
            maxBookingMins={freeForMinutes}
            isLoading={podInformationQuery.isLoading}
            estimatedPricing={podInformationQuery.data?.data?.estimatePriceAndHoldAmount}
          />
        );
    }
  }, [
    bookingCard,
    filter.date,
    ongoingReservationForPod,
    overlappingReservationForPod,
    ongoingProductReservationForPod,
    upcomingReservationForPod,
    unpaidProductReservationForPod,
    restReservationForPod,
    pod,
    podInformationQuery.data?.data?.estimatePriceAndHoldAmount,
    podInformationQuery.data?.data?.getPoisByID?.availabilityStatus,
    podInformationQuery.isLoading,
  ]);

  const renderCardFooter = useCallback(() => {
    if (bookingCard === 'USER_BOOKED_FOR_CURRENT_TIME_CARD') {
      return (
        <BookedCardFooter
          ongoingReservation={ongoingReservationForPod!}
          ongoingProductReservation={ongoingProductReservationForPod ?? undefined}
        />
      );
    }
  }, [bookingCard, ongoingProductReservationForPod, ongoingReservationForPod]);

  const handleInfoButton = useCallback(() => {
    if (pod.localRef) {
      navigation.navigate('map', {
        screen: screenNames.SpaceInfo,
        params: {
          spaceId: pod.localRef,
        },
      });
    }
  }, [navigation, pod.localRef]);

  return (
    <View style={{ backgroundColor: 'rgba(25,25,25,0.5)' }}>
      <ScrollView
        pointerEvents="auto"
        bounces={false}
        style={PixelRatio.getFontScale() > 1.5 ? { maxHeight: windowHeight / 2 } : {}}
      >
        <View style={[cardStyle.container, { backgroundColor: colors.surface }]}>
          <TouchableRipple
            accessibilityLabel={`${pod.title} #${pod.localRef}`}
            accessibilityRole="button"
            onPress={handleInfoButton}
            style={{
              paddingTop: 10,
              paddingHorizontal: 16,
            }}
          >
            <CardTitleRow space={pod} showInfoIcon={true} />
          </TouchableRipple>
          <Container>
            {showServices ? (
              <ServiceTable pod={pod} lockerInfo={lockerInfo} />
            ) : (
              <>
                {renderCardContent()}
                {!!pod.childrenRefs?.length && (
                  <Button icon={'magnifier'} style={{ marginTop: 8 }} onPress={onBrowseServicesPress}>
                    {I18n.t('mapview.podInfoCard.browseAndBookServices')}
                  </Button>
                )}
                {renderCardFooter()}
              </>
            )}
            <Spacer type={'section'} />
          </Container>
          <ContextMenuModal isVisible={isMenuRightVisible} setVisible={setIsMenuRightVisible} items={menuRightItems} />
        </View>
      </ScrollView>
    </View>
  );
};

const cardStyle = StyleSheet.create({
  container: {
    alignSelf: 'center',
    maxWidth: 400,
    width: '100%',
    marginVertical: 8,
    marginHorizontal: 8,
    zIndex: 1,
    borderRadius: 4,
  },
  imagePodDetail: {
    marginTop: -24,
    width: 80,
    height: 80,
  },
  titleSection: {
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingTop: 8,
  },
  title: {
    marginLeft: 20,
    flex: 1,
  },
  infoIcon: {
    marginRight: 0,
    marginTop: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    borderRadius: 0,
  },
});

export default PodInformationCard;
